import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Send, MessageCircle, Phone, MapPin } from 'lucide-react';

const ContactPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus('');

    try {
      // Email the contact form using mailto
      const mailtoLink = `mailto:support@luvcup.app?subject=${encodeURIComponent(formData.subject)}&body=${encodeURIComponent(
        `From: ${formData.name} (${formData.email})\n\n${formData.message}`
      )}`;
      
      window.location.href = mailtoLink;
      
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg px-6 py-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-rose-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-4">Get In Touch</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have questions, feedback, or need support? We'd love to hear from you!
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="glass-card p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <MessageCircle className="w-6 h-6 text-rose-600 mr-2" />
              Send Us a Message
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input-field w-full"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="input-field w-full"
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <select
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  className="input-field w-full"
                  required
                >
                  <option value="">Choose a topic...</option>
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Feature Request">Feature Request</option>
                  <option value="Bug Report">Bug Report</option>
                  <option value="Privacy & Data">Privacy & Data</option>
                  <option value="Partnership">Partnership Opportunity</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="input-field w-full min-h-[150px]"
                  placeholder="Tell us how we can help..."
                  required
                />
              </div>

              {status === 'success' && (
                <div className="bg-green-100 text-green-700 p-4 rounded-lg">
                  Message sent! We'll get back to you soon.
                </div>
              )}

              {status === 'error' && (
                <div className="bg-red-100 text-red-700 p-4 rounded-lg">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full flex items-center justify-center"
              >
                {sending ? (
                  <>
                    <div className="spinner w-4 h-4 mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Contact Information</h2>

              <div className="space-y-6">
                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-rose-600 mr-4 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Email Support</h3>
                    <p className="text-gray-600">support@luvcup.app</p>
                    <p className="text-sm text-gray-500 mt-1">
                      We typically respond within 24 hours
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <MessageCircle className="w-6 h-6 text-blue-600 mr-4 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">General Inquiries</h3>
                    <p className="text-gray-600">info@luvcup.app</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-purple-600 mr-4 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">Privacy & Legal</h3>
                    <p className="text-gray-600">privacy@luvcup.app</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick Links</h2>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/faq')}
                  className="block w-full text-left px-4 py-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg hover:shadow-md transition-shadow"
                >
                  <span className="font-semibold text-gray-800">FAQ</span>
                  <p className="text-sm text-gray-600">Find quick answers</p>
                </button>

                <button
                  onClick={() => navigate('/privacy')}
                  className="block w-full text-left px-4 py-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg hover:shadow-md transition-shadow"
                >
                  <span className="font-semibold text-gray-800">Privacy Policy</span>
                  <p className="text-sm text-gray-600">How we protect your data</p>
                </button>

                <button
                  onClick={() => navigate('/terms')}
                  className="block w-full text-left px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg hover:shadow-md transition-shadow"
                >
                  <span className="font-semibold text-gray-800">Terms of Service</span>
                  <p className="text-sm text-gray-600">Our usage terms</p>
                </button>
              </div>
            </div>

            <div className="glass-card p-8 bg-gradient-to-br from-rose-50 to-pink-50">
              <h3 className="font-semibold text-gray-800 mb-2">Response Time</h3>
              <p className="text-gray-600 text-sm">
                We aim to respond to all inquiries within 24 hours during business days. 
                For urgent issues, please mark your subject as "URGENT".
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;